import { triggerEvent } from "./helpers";

const generateFakeSlide = Symbol("generateFakeSlide");

const initTransitionEndEvent = () => {
  let t;
  const el = document.createElement("fakeelement");
  const transitions = {
    transition: "transitionend",
    OTransition: "oTransitionEnd",
    MozTransition: "transitionend",
    WebkitTransition: "webkitTransitionEnd",
  };

  for (t in transitions) {
    if (el.style[t] !== undefined) {
      return transitions[t];
    }
  }
};

const getParentByNode = (_elem, node) => {
  let elem = _elem;
  while (elem && elem.classList && elem !== node) {
    elem = elem.parentNode;
  }
  return elem && elem.head ? undefined : elem;
};

export default class Carousel {
  constructor(options = {}) {
    if (!options.mainContainer) return;

    this.options = {
      autoRotate: false,
      ...options,
    };

    const defaultItemsPerPage =
      parseInt(options.mainContainer.getAttribute("data-items-per-page"), 10) ||
      3;

    this.totalPages = this.options.totalPages || 1;
    this.perPage = this.options.perPage || defaultItemsPerPage;

    this.currentPage = 1;

    this.transitionEndEvent = initTransitionEndEvent();

    this.slides = [];

    this._boundHandleExternalScrollEvent =
      this.handleExternalScrollEvent.bind(this);

    this.init();

    this.widgetVisibilityThresholdPassed = false;
    this.widgetVisibilityPercentage = 0;

    this.controlsUpdateTimeout = "";
    this.carouselPaused = false;
  }

  getStepSize() {
    return this.perPage === 2 || this.perPage === 3 ? 1 : this.perPage;
  }

  recomputeTotalPages() {
    const step = this.getStepSize();

    if (!this.slides || this.slides.length === 0) {
      this.totalPages = this.options.totalPages || 1;
      return;
    }

    if (step === 1) {
      this.totalPages = Math.max(1, this.slides.length - this.perPage + 1);
    } else {
      this.totalPages = Math.max(
        1,
        Math.ceil(this.slides.length / this.perPage)
      );
    }
  }

  handleExternalScrollEvent(event) {
    if (!(event.data && event.data.type === "visibleEvent")) {
      return;
    }

    if (this.widgetVisibilityPercentage !== event.data.percentageVisible) {
      if (
        (this.widgetVisibilityPercentage > 50 &&
          event.data.percentageVisible <= 50) ||
        (this.widgetVisibilityPercentage < 50 &&
          event.data.percentageVisible >= 50)
      ) {
        this.widgetVisibilityThresholdPassed = true;
      }

      this.widgetVisibilityPercentage = event.data.percentageVisible;

      if (this.widgetVisibilityThresholdPassed) {
        this.widgetVisibilityThresholdPassed = false;
        this.toggleVisibleAutorotate(event);
      }
    }
  }

  init() {
    const { autoRotate, mainContainer } = this.options;

    this.tilesContainer = mainContainer.querySelector(".main-tiles-wrapper");
    this.nextBtn =
      mainContainer.parentElement.querySelector(".main-tiles__next");
    this.prevBtn =
      mainContainer.parentElement.querySelector(".main-tiles__prev");
    this.playBtn =
      mainContainer.parentElement.querySelector(".main-tiles__play");
    this.pauseBtn =
      mainContainer.parentElement.querySelector(".main-tiles__pause");

    if (
      !this.tilesContainer ||
      !this.nextBtn ||
      !this.prevBtn ||
      !this.playBtn ||
      !this.pauseBtn
    )
      return;

    this.nextBtn.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        this.nextPage();
      },
      false
    );

    this.prevBtn.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        this.prevPage();
      },
      false
    );

    this.pauseBtn.addEventListener("click", this.onPauseClick);
    this.playBtn.addEventListener("click", this.onPlayClick);

    this.updateControls();

    if (autoRotate) {
      this.initAutoRotate();
    }

    // init touch events
    let xdown = null;
    let ydown = null;

    function handletouchstart(evt) {
      xdown = evt.touches[0].clientX;
      ydown = evt.touches[0].clientY;
    }

    function handletouchmove(evt) {
      if (!xdown || !ydown) return;

      const xup = evt.touches[0].clientX;
      const yup = evt.touches[0].clientY;

      const xdiff = xdown - xup;
      const ydiff = ydown - yup;

      if (Math.abs(xdiff) > Math.abs(ydiff)) {
        if (xdiff > 0) {
          this.nextPage();
        } else {
          this.prevPage();
        }
      }

      xdown = null;
      ydown = null;
    }

    mainContainer.addEventListener("touchstart", handletouchstart, false);
    mainContainer.addEventListener(
      "touchmove",
      handletouchmove.bind(this),
      false
    );

    window.addEventListener("message", this._boundHandleExternalScrollEvent);
  }

  nextPage() {
    if (this.totalPages === 1) {
      return;
    }

    triggerEvent(
      this.options.mainContainer,
      "click.before.next.innocode-carousel.slide"
    );

    if (this.currentPage === this.totalPages) {
      this[generateFakeSlide]({ next: true });
      this.currentPage += 1;
      this.updateSlider();
    } else {
      this.incrementPage();
      this.updateSlider();
    }

    const that = this;
    let fallbackTimeoutTimerId;

    function onTransitionEndSendStatistic() {
      that.tilesContainer.removeEventListener(
        that.transitionEndEvent,
        onTransitionEndSendStatistic
      );
      clearTimeout(fallbackTimeoutTimerId);

      const start = that.currentPage - 1;
      const end = start + that.perPage;
      const currentSlidesNodes = that.slides.slice(start, end);

      triggerEvent(
        that.options.mainContainer,
        "click.after.next.innocode-carousel.slide",
        { currentSlidesNodes }
      );
    }

    // send statistic after some timeout, because not all browsers fire
    // transition end event when iframe is not in viewport
    fallbackTimeoutTimerId = setTimeout(onTransitionEndSendStatistic, 500);

    this.tilesContainer.addEventListener(
      this.transitionEndEvent,
      onTransitionEndSendStatistic
    );

    this.updatePrevControl();
  }

  prevPage() {
    if (this.totalPages === 1) {
      return;
    }

    triggerEvent(
      this.options.mainContainer,
      "click.before.prev.innocode-carousel.slide"
    );

    if (this.currentPage === 1) {
      this[generateFakeSlide]({ next: false });
      this.currentPage = 2;
      this.updateSlider(true);
      const timeout = setTimeout(() => {
        this.currentPage = 1;
        this.updateSlider();
        clearTimeout(timeout);
      }, 10);
    } else {
      this.decrementPage();
      this.updateSlider();
    }

    const that = this;

    this.tilesContainer.addEventListener(
      this.transitionEndEvent,
      function _removeAnimating() {
        that.tilesContainer.removeEventListener(
          that.transitionEndEvent,
          _removeAnimating
        );

        const start = that.currentPage - 1;
        const end = start + that.perPage;
        const currentSlidesNodes = that.slides.slice(start, end);

        triggerEvent(
          that.options.mainContainer,
          "click.after.prev.innocode-carousel.slide",
          { currentSlidesNodes }
        );
      }
    );

    this.updatePrevControl();
  }

  incrementPage() {
    this.currentPage =
      this.currentPage >= this.totalPages ? 1 : this.currentPage + 1;
  }

  decrementPage() {
    this.currentPage = this.currentPage === 1 ? 1 : this.currentPage - 1;
  }

  [generateFakeSlide]({ next }) {
    const step = this.getStepSize();
    let selectedNodes = [];

    if (next) {
      selectedNodes = this.slides.slice(0, step);
    } else {
      selectedNodes = this.slides.slice(-step);
    }

    const that = this;

    const slidesNodes = selectedNodes.reduce((acc, cur) => {
      acc.appendChild(cur.cloneNode(true));
      return acc;
    }, document.createDocumentFragment());

    if (next) {
      this.tilesContainer.appendChild(slidesNodes);
    } else {
      this.tilesContainer.insertBefore(
        slidesNodes,
        this.tilesContainer.firstChild
      );
    }

    this.tilesContainer.addEventListener(
      this.transitionEndEvent,
      function _removeFakeSlide() {
        that.tilesContainer.removeEventListener(
          that.transitionEndEvent,
          _removeFakeSlide
        );

        let nodesToRemove = [];

        that.currentPage = next ? 1 : that.totalPages;

        that.updateSlider(true);

        if (next) {
          nodesToRemove = [...that.tilesContainer.children].slice(-step);
        } else {
          nodesToRemove = [...that.tilesContainer.children].slice(0, step);
        }

        nodesToRemove.forEach((node) => {
          that.tilesContainer.removeChild(node);
        });
      }
    );
  }

  updateSlider(noAnimation = false) {
    if (!noAnimation) {
      this.tilesContainer.classList.add("js-animating");
      const that = this;

      this.tilesContainer.addEventListener(
        this.transitionEndEvent,
        function _removeAnimating() {
          that.tilesContainer.removeEventListener(
            that.transitionEndEvent,
            _removeAnimating
          );

          that.tilesContainer.classList.remove("js-animating");
        }
      );
    }

    const itemWidthPercent = 100 / this.perPage;
    const offset = -itemWidthPercent * (this.currentPage - 1);

    this.tilesContainer.style.webkitTransform = `translateX(${offset}%)`;
    this.tilesContainer.style.transform = `translateX(${offset}%)`;
  }

  updatePrevControl() {
    this.prevBtn.style.display =
      this.totalPages !== 1 &&
      (this.slides.length === this.totalPages * this.perPage ||
        this.currentPage > 1)
        ? "flex"
        : "none";
  }

  updateControls() {
    this.nextBtn.style.display = this.totalPages === 1 ? "none" : "flex";
    this.playBtn.style.display =
      this.totalPages !== 1 && this.carouselPaused ? "flex" : "none";
    this.pauseBtn.style.display =
      this.totalPages !== 1 && !this.carouselPaused ? "flex" : "none";
    this.updatePrevControl();
  }

  refreshSlides() {
    const tilesNodes = this.tilesContainer.childNodes;
    for (let i = 0; i < tilesNodes.length; i += 1) {
      tilesNodes[i].style.width = `${100 / this.perPage}%`;
    }
  }

  refreshSlider(refreshSlidesAlso) {
    this.updateSlider(); // move to the first slide
    this.updateControls(); // show/hide controls

    if (refreshSlidesAlso) this.refreshSlides(); // set slides (ads) width
  }

  onMouseOut = (event) => {
    const node = event.relatedTarget || event.toElement;
    const currentNode = event.currentTarget;

    if (
      node &&
      node.parentNode &&
      getParentByNode(node, currentNode) === currentNode
    )
      return;

    this.enableAutoRotate();
  };

  onMouseEnter = () => {
    this.disableAutoRotate();
  };

  onPlayClick = () => {
    this.carouselPaused = false;

    this.initAutoRotate();

    this.playBtn.style.display = "none";
    this.pauseBtn.style.display = "flex";
  };

  onPauseClick = () => {
    this.carouselPaused = true;
    this.deinitAutoRotate();

    this.playBtn.style.display = "flex";
    this.pauseBtn.style.display = "none";
  };

  onKeyboardNavigationOn = () => {
    clearTimeout(this.controlsUpdateTimeout);
    this.deinitAutoRotate();
    this.prevBtn.style.display = "none";
    this.nextBtn.style.display = "none";
    this.playBtn.style.display = "none";
    this.pauseBtn.style.display = "none";

    this.tilesContainer.style.transform = "translateX(0%)";
    this.currentPage = 1;
  };

  onKeyboardNavigationOff = () => {
    this.currentPage = 1;
    this.tilesContainer.style.transform = "translateX(0%)";
    this.prevBtn.style.display = "flex";
    this.nextBtn.style.display = "flex";
    this.pauseBtn.style.display = "flex";
    this.carouselPaused = false;

    this.initAutoRotate();

    this.controlsUpdateTimeout = setTimeout(() => {
      if (this.prevBtn && this.nextBtn) this.updateControls();
      clearTimeout(this.controlsUpdateTimeout);
    }, 0);
  };

  initAutoRotate = () => {
    if (this.carouselPaused || this.totalPages === 1) return;
    this.enableAutoRotate();
    this.options.mainContainer.addEventListener(
      "mouseenter",
      this.onMouseEnter
    );
    this.options.mainContainer.addEventListener(
      "mouseout",
      this.onMouseOut,
      true
    );
  };

  deinitAutoRotate = () => {
    this.disableAutoRotate();
    this.options.mainContainer.removeEventListener(
      "mouseout",
      this.onMouseOut,
      true
    );
    this.options.mainContainer.removeEventListener(
      "mouseenter",
      this.onMouseEnter
    );
  };

  enableAutoRotate() {
    if (this.carouselPaused || this.totalPages === 1) return;

    this.disableAutoRotate();

    this.intervalPoint = setInterval(() => {
      this.nextPage();
    }, 2500);
  }

  toggleVisibleAutorotate = (event) => {
    if (event.data.percentageVisible <= 50) {
      this.deinitAutoRotate();
    } else {
      this.initAutoRotate();
    }
  };

  disableAutoRotate() {
    clearInterval(this.intervalPoint);
  }

  getCurrentSlide() {
    return this.currentPage;
  }

  setTotalPages(data) {
    this.totalPages = data;

    this.recomputeTotalPages();
    this.refreshSlider();
  }

  setPerPage(data) {
    this.perPage = data;
    this.refreshSlides();
    this.recomputeTotalPages();
    this.refreshSlider();
  }

  setCurrentSlide(data) {
    this.currentPage = data;
    this.updateSlider();
  }

  getSlidesContainer() {
    return this.tilesContainer;
  }

  addSlides(nodes) {
    const { mainContainer } = this.options;

    if (nodes.length <= 0) return;

    const slidesNodes = nodes.reduce((acc, cur) => {
      const wrapperNode = document.createElement("div");
      wrapperNode.classList.add("main-tiles-unit-wrapper");
      wrapperNode.style.width = `${100 / this.perPage}%`;
      wrapperNode.appendChild(cur);

      acc.appendChild(wrapperNode);
      this.slides = [...this.slides, wrapperNode];

      return acc;
    }, document.createDocumentFragment());

    this.tilesContainer.appendChild(slidesNodes);
    triggerEvent(mainContainer, "add-slides.after.innocode-carousel");
  }

  addSlides(nodes) {
    const { mainContainer } = this.options;

    if (!nodes || nodes.length <= 0) return;

    const slidesNodes = nodes.reduce((acc, cur) => {
      const wrapperNode = document.createElement("div");
      wrapperNode.classList.add("main-tiles-unit-wrapper");
      wrapperNode.style.width = `${100 / this.perPage}%`;
      wrapperNode.appendChild(cur);

      acc.appendChild(wrapperNode);
      this.slides = [...this.slides, wrapperNode];

      return acc;
    }, document.createDocumentFragment());

    this.tilesContainer.appendChild(slidesNodes);

    this.recomputeTotalPages();
    this.refreshSlider();
    this.initAutoRotate();

    triggerEvent(mainContainer, "add-slides.after.innocode-carousel");
  }
}
